import {
  COMMON_DENSITY,
  EVOLUTION_GROWTH_EXPONENT,
  PHASE_SIZE_MULTIPLIERS,
  PHASE_TRIGGER_STAGES,
} from '../../config/gameplay';
import type { ResonancePhase } from '../simulation/dropBag';
import {
  compoundCentroid,
  normalizedCompoundArea,
  type ConvexPartDefinition,
  type Point,
} from '../geometry';
import { GENERATED_COLLISION_PROFILES } from './generatedCollisionProfiles';
import { OBSERVER_STAGE } from './progression';
import { DEFAULT_GAME_MODE, getModePhase, presentationPhase, type GameModeId } from './modes';

// Reference sizes account for each collision silhouette, not its square texture.
// The first resonance's endpoints anchor the accelerating area curve below;
// getCharacterForPhase reuses that curve at every resonance.
// These artwork calibration sizes are retained as reference values, including
// out-of-phase stages used by inspection tools. See character-size-report.json.
export const CHARACTER_ROWS = [
  ['weak-stone', '弱い石', 1, 1, 1, 19.183, null, 1],
  ['proliferation-stone', '増殖石', 2, 4, 4, 32.45, null, 1],
  ['ghost-stone', '幽体石', 3, 5, 5, 46.855, null, 1],
  ['hyperactive-stone', '躍動石', 4, 5, 5, 53.887, null, 1],
  ['sacrifice-stone', '犠牲石', 5, 6, 6, 66.806, null, 1],
  ['escape-stone', '逃亡石', 6, 7, 7, 77.956, null, 1],
  ['destruction-dragon', '破壊龍', 7, 7, 7, 89.657, null, 1],
  ['afterimage-stone', '残像石', 8, 8, 8, 104.297, null, 1],
  ['labor-stone', '労働石', 9, 11, 11, 198.33, null, 1],
  ['breeding-stone', '繁殖石', 10, 12, 12, 123.51, null, 1],
  ['revival-stone', '復活石', 11, 12, 12, 118.264, null, 1],
  ['water-stone', '水石', 12, 15, 15, 121.815, null, 1],
  ['strong-stone', '強い石', 13, 16, 16, 132.722, null, 1],
  ['robot-vacuum-stone', 'ロボット掃除機石', 14, 17, 17, 131.38, null, 1],
  ['corpse-stone', '屍石', 15, 19, 19, 160.373, null, 1],
  ['grass-stone', '草石', 16, 20, 20, 156.201, null, 1],
  ['salvation-god', '救済神', 17, 20, 20, 159.207, null, 1],
  ['fire-stone', '火石', 18, 21, 21, 198.162, null, 1],
  ['sniper-stone', '狙撃石', 19, 23, 23, 167.002, null, 1],
  ['ultimate-labor-god', '究極労働神', 20, 25, 25, 269.391, null, 1],
  ['lightning-stone', '落雷石', 21, 26, 26, 217.613, null, 1],
  ['ultimate-hyperactive-god', '究極躍動神', 22, 28, 28, 213.611, null, 1],
  ['glutton-stone', '悪食石', 23, 29, 29, 164.648, null, 1],
  ['ultimate-reversal-dragon', '究極反転龍', 24, 30, 30, 224.669, null, 1],
  ['ultimate-destruction-god', '究極破壊神', 25, 30, 30, 258.702, null, 1],
  ['extreme-hyperactive-demon', '極悪躍動魔', 26, 32, 32, 245.725, null, 1],
  ['will-hunter-king', '意志狩りの王', 27, 33, 33, 198.111, null, 1],
  ['causality-erasure-god-stone', '因果抹消神石', 28, 40, 40, 276.633, null, 1],
  ['shinra-bansho-god', '森羅万象神', 29, null, 50, 211.766, 'shinra', 2],
  ['board-logic-observer', '盤理の観測者', 30, null, 60, 275.014, 'observer', 5],
  ['board-executor', '盤界の執行者', 31, null, 70, 275.014, null, 5],
  ['theory-incarnation', '理論の化身', 32, null, 80, 275.014, null, 5],
  ['end-ashes', '終焉の冥灰', 33, null, 90, 275.014, null, 5],
] as const;

export type CharacterId = (typeof CHARACTER_ROWS)[number][0];
export type CharacterSpecialKind = (typeof CHARACTER_ROWS)[number][6];

export type CharacterDefinition = Readonly<{
  id: CharacterId;
  name: string;
  stage: number;
  cost: number | null;
  scoreCost: number;
  specialKind: CharacterSpecialKind;
  scoreMultiplier: 1 | 2 | 5;
  baseSize: number;
  mass: number;
  centerOfMass: Point;
  initialAngle: number;
  collisionParts: readonly ConvexPartDefinition[];
  collisionAgreement: number;
  textureKey: `character-${CharacterId}`;
  dangerCore: Readonly<{ offsetX: number; offsetY: number; radiusRatio: number }>;
  nextId: CharacterId | null;
}>;

function rounded(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}

// Enlarge the previous 1.2 scale by another 20%; rendering, collisions, and mass share it.
const CHARACTER_SIZE_SCALE = 1.44;

const referenceCharacters: readonly CharacterDefinition[] = CHARACTER_ROWS.map(
  ([id, name, stage, cost, scoreCost, progressionSize, specialKind, scoreMultiplier], index) => {
    const baseSize = rounded(progressionSize * CHARACTER_SIZE_SCALE);
    const generated = GENERATED_COLLISION_PROFILES[id];
    const collisionParts: readonly ConvexPartDefinition[] = generated.parts.map((part) => ({
      points: part.map(([x, y]) => ({ x, y })),
    }));
    const normalizedArea = normalizedCompoundArea(collisionParts);
    const profileCenter = compoundCentroid(collisionParts);
    return {
      id,
      name,
      stage,
      cost,
      scoreCost,
      specialKind,
      scoreMultiplier,
      baseSize,
      mass: normalizedArea * baseSize * baseSize * COMMON_DENSITY,
      centerOfMass: { x: rounded(profileCenter.x), y: rounded(profileCenter.y) },
      // Preserve each sprite's authored pose; contacts can rotate it naturally.
      initialAngle: 0,
      collisionParts,
      collisionAgreement: generated.agreement,
      textureKey: `character-${id}`,
      dangerCore: {
        offsetX: rounded((((stage * 3) % 5) - 2) * 0.008),
        offsetY: rounded(0.02 + (stage % 3) * 0.008),
        radiusRatio: rounded(0.22 + (stage % 4) * 0.012),
      },
      nextId: CHARACTER_ROWS[index + 1]?.[0] ?? null,
    } satisfies CharacterDefinition;
  },
);

const firstGrowthCharacter = referenceCharacters[0]!;
const lastGrowthCharacter = referenceCharacters[PHASE_TRIGGER_STAGES[0] - 1]!;

export const CHARACTERS: readonly CharacterDefinition[] = referenceCharacters.map((character) => {
  if (character.stage === 1 || character.stage >= lastGrowthCharacter.stage) return character;
  const progress = (character.stage - 1) / (lastGrowthCharacter.stage - 1);
  // Interpolate logarithmic occupied area, keeping both endpoint sizes fixed.
  // Increasing area ratios make late evolutions substantial while every merge
  // still occupies less space than its two ingredients. Common density lets
  // mass express the same curve independently of each artwork's silhouette.
  const mass = firstGrowthCharacter.mass
    * (lastGrowthCharacter.mass / firstGrowthCharacter.mass) ** (progress ** EVOLUTION_GROWTH_EXPONENT);
  const baseSize = character.baseSize * Math.sqrt(mass / character.mass);
  return { ...character, baseSize, mass };
});

export const CHARACTER_BY_ID = new Map(CHARACTERS.map((character) => [character.id, character]));
export const CHARACTER_BY_STAGE = new Map(CHARACTERS.map((character) => [character.stage, character]));

export function getCharacter(id: CharacterId): CharacterDefinition {
  const character = CHARACTER_BY_ID.get(id);
  if (!character) throw new Error(`Unknown character: ${id}`);
  return character;
}

export function getCharacterAtStage(stage: number): CharacterDefinition {
  const character = CHARACTER_BY_STAGE.get(stage);
  if (!character) throw new Error(`Unknown character stage: ${stage}`);
  return character;
}

const phaseCharacters = new Map<string, CharacterDefinition>();

/** Each resonance scales the calibrated growth curve, including phase 1.
 * Stage 10, for example, becomes local stage 5 when phase 2 starts.
 * Normalize collision area before applying the phase multiplier so difficulty
 * grows deliberately rather than depending on the artwork's proportions.
 */
export function getCharacterForPhase(id: CharacterId, phase: ResonancePhase, mode: GameModeId = DEFAULT_GAME_MODE): CharacterDefinition {
  const character = getCharacter(id);
  const key = `${mode}:${phase}:${id}`;
  const cached = phaseCharacters.get(key);
  if (cached) return cached;
  if (character.stage > OBSERVER_STAGE) {
    // Hidden evolutions retain the Observer's display size in every phase.
    // Their own silhouettes still determine collisions and physical mass.
    const baseSize = getCharacterForPhase('board-logic-observer', phase, mode).baseSize;
    const mass = normalizedCompoundArea(character.collisionParts) * baseSize ** 2 * COMMON_DENSITY;
    const scaled = { ...character, baseSize, mass };
    phaseCharacters.set(key, scaled);
    return scaled;
  }
  // Short chapters start two sizes further up the existing curve so their
  // three drop types remain tactile without inflating a merge beyond its inputs.
  const localStage = Math.max(1, character.stage - getModePhase(mode, phase).firstStage + 1 + (mode === 'five-resonance' ? 2 : 0));
  const reference = getCharacterAtStage(localStage);
  const phaseScale = PHASE_SIZE_MULTIPLIERS[presentationPhase(mode, phase) - 1]!;
  const scale = Math.sqrt(reference.mass / character.mass) * phaseScale;
  const baseSize = character.baseSize * scale;
  const scaled = { ...character, baseSize, mass: reference.mass * phaseScale ** 2 };
  phaseCharacters.set(key, scaled);
  return scaled;
}
